import React, { useEffect, useMemo, useState } from "react";
import { productDraftApiService } from "../../../service/admin/productDraftApiService";
import { productErrorApiService } from "../../../service/admin/productErrorApiService";
import { productListApiService } from "../../../service/admin/productApiService";
import { ProductsApiService } from "../../../service/website/productsApiService";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { MdDelete, MdModeEdit, MdDescription } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaSync, FaDownload, FaCheckCircle, FaTrash } from "react-icons/fa";
import DeletePopUp from "../../../component/library/deletePopUp";
import ExcelTable from "../../../component/library/excelTable";
import { exportToCSV } from "react-easy-export";
import ExcelJS from "exceljs";
import { getAuthLocalStorage } from "../../../storage/localStorage";
// import Logo from "../../../website/assets/images/common/logo_no_image.png";
import "./style.css";
import { generateProductUrl } from '../../../utils/productUrlUtils';

export default function ProductErrorList() {
  const [productErrorData, setProductErrorData] = useState([]);
  const [filters, setFilters] = useState({ search: "", status: "", source: "" });
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
  const [open, setOpen] = useState(false);
  const [id, setId] = useState("");
  const [deleteStatus, setDeleteStatus] = useState("");
  const [selectedDrafts, setSelectedDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [translatedSellerNames, setTranslatedSellerNames] = useState({});
  const [exportProcessing, setExportProcessing] = useState(false);
  const [exportStatus, setExportStatus] = useState("");
  const [exportProgress, setExportProgress] = useState(0);
  const navigate = useNavigate();
  const loader = useLoader();
  const message = useMessage();

  const authData = getAuthLocalStorage();
  const currentUserId = authData?.loginHeader || null;

  const handleOnChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });

  const normalizeUrl = (url) => {
    if (!url) return "";
    try { return String(url).replace(/^http:\/\//, "https://"); } catch { return url; }
  };

  const numericValue = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (!value) return 0;
    const parsed = parseFloat(String(value).replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatPrice = (value, symbol = "₹") => {
    if (value == null || !Number.isFinite(numericValue(value))) return null;
    const num = numericValue(value);
    return `${symbol}${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getVariantMixPrices = (item) => {
    const vars = item?.variations || [];
    if (!vars.length) {
      const vp = numericValue(item.vendor_price);
      return { minAp: vp, maxAp: vp, minCp: null, maxCp: null };
    }
    let minAp = null, maxAp = null, minCp = null, maxCp = null;
    for (const v of vars) {
      const ap = typeof v.ap === "number" && Number.isFinite(v.ap) ? v.ap : (typeof v.price === "number" && Number.isFinite(v.price) ? v.price : null);
      const cp = typeof v.cp === "number" && Number.isFinite(v.cp) ? v.cp : (typeof v.consignPrice === "number" && Number.isFinite(v.consignPrice) ? v.consignPrice : null);
      if (ap != null) {
        if (minAp == null || ap < minAp) minAp = ap;
        if (maxAp == null || ap > maxAp) maxAp = ap;
      }
      if (cp != null) {
        if (minCp == null || cp < minCp) minCp = cp;
        if (maxCp == null || cp > maxCp) maxCp = cp;
      }
    }
    if (minAp == null) minAp = numericValue(item.vendor_price);
    if (maxAp == null) maxAp = minAp;
    return { minAp, maxAp, minCp, maxCp };
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return "-"; }
  };

  const truncateText = (text = "", maxLength = 50) => {
    if (typeof text !== "string") return "";
    return text.length > maxLength ? `${text.substring(0, maxLength)}…` : text;
  };

  // Helper function to convert column index (0-based) to Excel column letter (A, B, C, ..., Z, AA, AB, ...)
  const getExcelColumnLetter = (colIndex) => {
    let result = '';
    let num = colIndex;
    while (num >= 0) {
      result = String.fromCharCode(65 + (num % 26)) + result;
      num = Math.floor(num / 26) - 1;
    }
    return result;
  };

  const handleExportCsv = async () => {
    setExportProcessing(true);
    setExportProgress(0);
    setExportStatus("Preparing export...");
    try {
    const source = selectedDrafts.length ? selectedDrafts : tableRows;
      
      if (source.length === 0) {
        message.showToast("No data to export", ToastTypes.WARNING);
        setExportProcessing(false);
        return;
      }
      
      // Use existing data from table (no need to fetch again)
      setExportProgress(10);
      setExportStatus(`Preparing ${source.length} product(s) for export...`);
      const fullProductData = source.map((entry) => {
        // Use raw data that's already available in the table
        return entry.raw || entry;
      });

      // First pass: Find maximum number of attributes in any single variant
      let maxAttributeCount = 0;
      fullProductData.forEach((product) => {
        const variations = product.variations || [];
        variations.forEach((variant) => {
          if (variant && Array.isArray(variant.skuAttributes)) {
            const attributeCount = variant.skuAttributes.filter(attr => {
              const attrName = attr.attributeNameTrans || attr.attributeName || "";
              const attrValue = attr.valueTrans || attr.value || "";
              return attrName && attrValue;
            }).length;
            if (attributeCount > maxAttributeCount) {
              maxAttributeCount = attributeCount;
            }
          }
        });
      });

      // Find maximum imgUrl array length across all products
      let maxImgUrlLength = 0;
      fullProductData.forEach((product) => {
        const imgUrlLength = Array.isArray(product.imgUrl) ? product.imgUrl.length : 0;
        if (imgUrlLength > maxImgUrlLength) {
          maxImgUrlLength = imgUrlLength;
        }
      });

      // Create export data with one row per variant
      const exportData = [];
      
      fullProductData.forEach((product) => {
        const variations = product.variations || [];
        // Normalize image URL
        const mainImage = product.imgUrl?.[0] ? normalizeUrl(product.imgUrl[0]) : "";
        const productSku = product.sku || product.parentSku || "";

        // Helper function to extract attributes from skuAttributes as ordered array
        const extractAttributesArray = (variant) => {
          const attributes = [];
          if (variant && Array.isArray(variant.skuAttributes)) {
            variant.skuAttributes.forEach((attr) => {
              const attrName = attr.attributeNameTrans || attr.attributeName || "";
              const attrValue = attr.valueTrans || attr.value || "";
              if (attrName && attrValue) {
                attributes.push(`${attrName} - ${attrValue}`);
              }
            });
          }
          return attributes;
        };

        // Helper function to create export row
        const createExportRow = (variant = null) => {
          // Use variant image if available, otherwise use product image
          const variantImage = variant?.imgUrl?.[0] || variant?.image || "";
          const displayImage = variantImage ? normalizeUrl(variantImage) : mainImage;
          
          // Extract attributes from skuAttributes as ordered array
          const variantAttributesArray = variant ? extractAttributesArray(variant) : [];
          
          // Extract image description from variant skuAttributes
          const imageDescription = variant && Array.isArray(variant.skuAttributes) 
            ? variant.skuAttributes.find(attr => attr.skuImageUrl)?.skuImageUrl || ""
            : "";
          
          const row = {
            "Image": "", // Blank column for formula
            "Image URL": displayImage,
            "Product Name": product.title || "",
            "Product SKU": (() => {
              // Keep as string to prevent scientific notation in Excel
              if (productSku) {
                return String(productSku);
              }
              return "";
            })(),
            "Variant SKU": variant ? (() => {
              // Keep as string to prevent scientific notation in Excel
              const sku = variant.sku || variant.skuId || variant.specId || "";
              if (sku) {
                return String(sku);
              }
              return "";
            })() : (() => {
              // Keep as string to prevent scientific notation in Excel
              const sku = product.sku || "";
              if (sku) {
                return String(sku);
              }
              return "";
            })(),
            // Dynamic attribute columns - based on maximum count, fill in order
            ...(Array.from({ length: maxAttributeCount }, (_, index) => {
              const attrValue = variantAttributesArray[index] || "";
              return { [`Attribute ${index + 1}`]: attrValue };
            }).reduce((acc, obj) => ({ ...acc, ...obj }), {})),
            "Category": "",
            "Quantity": variant ? (variant.amountOnSale || 0) : (product.quantity || 0),
            "Weight": variant ? (variant.weight || 0) : (product.weight || 0),
            "Length": variant ? (variant.length || 0) : (product.length || ""),
            "Width": variant ? (variant.width || 0) : (product.width || ""),
            "Height": variant ? (variant.height || 0) : (product.height || ""),
            "AP": variant ? (variant.ap || variant.actualPrice || "") : "",
            "CP": variant ? (variant.consignPrice || variant.cp || "") : "",
            "DS Final Price": variant ? formatPrice(variant.finalPrice || 0) : formatPrice(product.sp || 0),
            "WS Final Price": variant ? formatPrice(variant.wholesaleFinalPrice || 0) : "",
            "Variant Image": "", // Blank column for formula
            "Variant Image URL": variant && Array.isArray(variant.skuAttributes) 
              ? (variant.skuAttributes.find(attr => attr.skuImageUrl)?.skuImageUrl || "") 
              : "",
            "Offer ID": product.offerId || "",
            "GST": product.gst || "",
            "HSN": product.hsn || "",
            "Handling Days": product.handling_days || "",
            "Max Order Quantity": product.max_order_quantity || product.moq || "",
            "Local Shipping": variant ? (variant.localShipping || variant.ls || 0) : (product.local_shipping || 0),
            "Generic Keywords": product.generic_keywords || "",
            "Search Keywords": Array.isArray(product.keywordSearch) ? product.keywordSearch.join(", ") : (product.keywordSearch || ""),
            "Bullet Point 1": Array.isArray(product.bullet_points) ? (product.bullet_points[0] || "") : "",
            "Bullet Point 2": Array.isArray(product.bullet_points) ? (product.bullet_points[1] || "") : "",
            "Bullet Point 3": Array.isArray(product.bullet_points) ? (product.bullet_points[2] || "") : "",
            "Bullet Point 4": Array.isArray(product.bullet_points) ? (product.bullet_points[3] || "") : "",
            "Bullet Point 5": Array.isArray(product.bullet_points) ? (product.bullet_points[4] || "") : "",
            // Dynamic image columns based on imgUrl array length - blank column + URL column
            ...(Array.from({ length: maxImgUrlLength }, (_, index) => {
              const imgUrl = Array.isArray(product.imgUrl) && product.imgUrl[index] 
                ? normalizeUrl(product.imgUrl[index]) 
                : "";
              return { 
                [`Img ${index + 1}`]: "", // Blank column for formula
                [`Img ${index + 1} URL`]: imgUrl
              };
            }).reduce((acc, obj) => ({ ...acc, ...obj }), {})),
            "Description": product.description || "",
            "Image Description": product.img_description || "",
          };
          return row;
        };

        // If product has no variants, create one row with product data
        if (variations.length === 0) {
          exportData.push(createExportRow());
        } else {
          // Create one row for each variant
          variations.forEach((variant) => {
            exportData.push(createExportRow(variant));
          });
        }
      });

      // Export to Excel with column width based on content
      if (exportData.length === 0) {
        message.showToast("No data to export", ToastTypes.WARNING);
        setExportProcessing(false);
        return;
      }

      setExportProgress(30);
      setExportStatus(`Preparing Excel file with ${exportData.length} rows...`);
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Product Drafts');

      // Get all column keys from first row
      const columnKeys = Object.keys(exportData[0]);
      
      // Define columns with headers
      const columns = columnKeys.map((key) => ({
        header: key,
        key: key,
        width: 15, // Default width, will be adjusted
      }));

      worksheet.columns = columns;

      // Style header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' }
      };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      headerRow.height = 20;

      setExportProgress(40);
      setExportStatus(`Adding data rows...`);
      // Add data rows
      for (let rowIndex = 0; rowIndex < exportData.length; rowIndex++) {
        // Update progress (70% to 90%)
        const dataProgress = 70 + Math.floor((rowIndex / exportData.length) * 20);
        setExportProgress(dataProgress);
        const rowData = exportData[rowIndex];
        const row = worksheet.addRow(rowData);
        const excelRowIndex = rowIndex + 2; // +2 because row 1 is header
        
        // Set row height
        row.height = 25;
        
        // Process each cell
        row.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.alignment = { vertical: 'middle', horizontal: 'left' };
          
          // Format SKU columns as text to prevent scientific notation
          const columnKey = columnKeys[colNumber - 1];
          if (columnKey === 'Product SKU' || columnKey === 'Variant SKU') {
            cell.numFmt = '@'; // '@' is the text format code in Excel
            // Ensure value is treated as text
            if (cell.value !== null && cell.value !== undefined) {
              cell.value = String(cell.value);
            }
          }
        });
      }
      
      // Auto-fit columns based on content length
      let descriptionColumnWidth = null;
      
      // First pass: calculate all column widths and find Description column width
      columnKeys.forEach((key, index) => {
        const column = worksheet.getColumn(index + 1);
        
        // Set width for Image columns (blank columns for formulas)
        if (key === 'Image' || key === 'Variant Image' || (key.startsWith('Img ') && !key.endsWith(' URL'))) {
          column.width = 15; // Fixed width for blank image columns
          return;
        }
        
        // Set width for Image URL columns (containing URLs)
        if (key === 'Image URL' || key === 'Variant Image URL' || (key.startsWith('Img ') && key.endsWith(' URL'))) {
          let maxUrlLength = key.length; // Start with header length
          worksheet.getColumn(key).eachCell({ includeEmpty: false }, (cell) => {
            const cellValue = cell.value ? String(cell.value) : '';
            if (cellValue.length > maxUrlLength) {
              maxUrlLength = cellValue.length;
            }
          });
          const urlWidth = Math.min(50, Math.max(15, maxUrlLength + 5));
          column.width = urlWidth;
          return;
        }
        
        let maxLength = key.length; // Start with header length
        
        // Find max content length in this column
        worksheet.getColumn(key).eachCell({ includeEmpty: false }, (cell) => {
          const cellValue = cell.value ? String(cell.value) : '';
          if (cellValue.length > maxLength) {
            maxLength = cellValue.length;
          }
        });
        
        // Set width with padding (min 10, max 60 for better readability)
        const calculatedWidth = Math.min(60, Math.max(10, maxLength + 3));
        column.width = calculatedWidth;
        
        // Store Description column width
        if (key === 'Description') {
          descriptionColumnWidth = calculatedWidth;
        }
      });
      
      // Second pass: Set Image Description column width to match Description column width
      if (descriptionColumnWidth !== null) {
        columnKeys.forEach((key, index) => {
          if (key === 'Image Description') {
            const column = worksheet.getColumn(index + 1);
            column.width = descriptionColumnWidth;
          }
        });
      }

      // Generate buffer and download
      setExportProgress(80);
      setExportStatus("Generating Excel file...");
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Product_Drafts_Export.xlsx';
      link.style.display = 'none';
      
      // Append to body, trigger download, then remove
      document.body.appendChild(link);
      
      setExportProgress(95);
      setExportStatus("Downloading file...");
      
      // Use setTimeout to ensure the link is properly attached
      setTimeout(() => {
        link.click();
        setExportProgress(100);
        setExportStatus("Export completed!");
        
        // Clean up after a short delay
        setTimeout(() => {
          // Check if link still exists in DOM before removing
          if (link && link.parentNode) {
            try {
              document.body.removeChild(link);
            } catch (error) {
              console.warn("Link already removed:", error);
            }
          }
          // Always revoke URL to free memory
          if (url) {
            try {
              window.URL.revokeObjectURL(url);
            } catch (error) {
              console.warn("Error revoking URL:", error);
            }
          }
          message.showToast(`Exported ${exportData.length} rows (${fullProductData.length} products)`, ToastTypes.SUCCESS);
          setExportProcessing(false);
        }, 500);
      }, 100);
    } catch (error) {
      console.error("Export error:", error);
      message.showToast("Error exporting data: " + (error.message || "Unknown error"), ToastTypes.ERROR);
      setExportProcessing(false);
    }
  };

  const handleBulkPublish = async () => {
    if (!selectedDrafts.length) {
      message.showToast("Select drafts to publish", ToastTypes.WARNING);
      return;
    }
    loader.showLoading(true);
    try {
      const results = await Promise.all(
        selectedDrafts.map((entry) => {
          const item = entry.raw || entry;
          return productDraftApiService.updateProductDraft(item._id, { status: "5" })
            .then((res) => res?.status?.code === 200 ? "fulfilled" : "rejected")
            .catch(() => "rejected");
        })
      );
      const successCount = results.filter((s) => s === "fulfilled").length;
      if (successCount) message.showToast(`${successCount} draft(s) published`, ToastTypes.SUCCESS);
      setSelectedDrafts([]);
      fetchDraftsList();
    } finally {
      loader.showLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedDrafts.length) {
      message.showToast("Select drafts to delete", ToastTypes.WARNING);
      return;
    }
    if (!window.confirm(`Delete ${selectedDrafts.length} draft(s)?`)) return;
    loader.showLoading(true);
    try {
      const results = await Promise.all(
        selectedDrafts.map((entry) => {
          const item = entry.raw || entry;
          const itemStatus = String(item?.status || item?.processingStatus || "").trim();
          const deleteApi = itemStatus === "5"
            ? productListApiService.deleteProducts
            : productDraftApiService.deleteProductDraft;

          return deleteApi(item._id)
            .then((res) => res?.status?.code === 200 ? "fulfilled" : "rejected")
            .catch(() => "rejected");
        })
      );
      const successCount = results.filter((s) => s === "fulfilled").length;
      if (successCount) message.showToast(`${successCount} draft(s) deleted`, ToastTypes.SUCCESS);
      setSelectedDrafts([]);
      fetchDraftsList();
    } finally {
      loader.showLoading(false);
    }
  };

  const buildQueryParams = () => {
    let q = `?page=${page}&limit=${limit}&sortBy=createdAt&sortOrder=desc&hasError=true`;
    if (filters.search) q += `&search=${encodeURIComponent(filters.search)}`;
    if (filters.status) q += `&status=${encodeURIComponent(filters.status)}`;
    if (filters.source) q += `&source=${encodeURIComponent(filters.source)}`;
    return q;
  };

  // Translate Chinese seller (company) names to English
  const translateSellerNames = async (drafts) => {
    const chineseNames = {};
    (drafts || []).forEach((item) => {
      const name = item.sellerCompanyName || item.companyName || "";
      if (name && /[\u4e00-\u9fa5]/.test(name) && !translatedSellerNames[name]) {
        chineseNames[name] = name;
      }
    });
    if (Object.keys(chineseNames).length === 0) return;
    try {
      const translateData = { sellerNames: chineseNames };
      const res = await ProductsApiService.translateMandarinToEnglish(translateData, "en");
      if (res.status?.code === 200 && res.data?.sellerNames) {
        setTranslatedSellerNames((prev) => ({ ...prev, ...res.data.sellerNames }));
      }
    } catch (error) {
      console.error("Translation error:", error);
    }
  };

  const fetchDraftsList = () => {
    setLoading(true);
    productErrorApiService.getAllProductErrors(buildQueryParams())
      .then((res) => {
        setLoading(false);
        setInitialLoading(false);
        if (res.status.code === 200) {
          setProductErrorData(res.data.drafts);
          setPages({
            currentPage: res.data.pagination.currentPage,
            totalPages: res.data.pagination.totalPages,
            totalItems: res.data.pagination.totalItems,
          });
          setPage(res.data.pagination.currentPage);
          // commenting out for now temporarily enabling it later.
          // translateSellerNames(res.data.drafts);
        } else if (res.status.code === 402) {
          navigate("/login");
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Server error", ToastTypes.ERROR);
        setLoading(false);
        setInitialLoading(false);
      });
  };

  useEffect(() => { fetchDraftsList(); }, [page, limit]);

  const searchDrafts = () => { setPage(1); fetchDraftsList(); };
  const handleEdit = (id, status) => {
    if (status === "5") {
      // Status 5 = List - open product list edit page
      navigate(`/admin/products/edit/${id}`);
    } else {
      // Status 6 = Draft - open draft edit page
      navigate(`/admin/product-drafts/edit/${id}`);
    }
  };

  const handleDimensionsEdit = (item) => {
    // Navigate to detail page for dimension editing in new tab
    if (item?._id) {
      window.open(`/admin/product-errors/${item._id}`, '_blank');
    }
  };

  const handleOpenSelectedDetails = () => {
    if (!selectedDrafts.length) {
      message.showToast("Select at least one product", ToastTypes.WARNING);
      return;
    }

    const ids = selectedDrafts
      .map((entry) => entry?.raw?._id || entry?._id)
      .filter(Boolean);

    if (!ids.length) {
      message.showToast("No valid selected products", ToastTypes.WARNING);
      return;
    }

    if (ids.length === 1) {
      window.open(`/admin/product-errors/${ids[0]}`, '_blank');
      return;
    }

    window.open(`/admin/product-errors/details?ids=${encodeURIComponent(ids.join(","))}`, '_blank');
  };


  const handleView = (id) => navigate(`/admin/product-drafts/view/${id}`);
  const handleDelete = (id, status) => { setId(id); setDeleteStatus(String(status || "").trim()); setOpen(true); };
  const handleClose = () => { setOpen(false); setId(""); setDeleteStatus(""); };

  const handleSubmit = () => {
    loader.showLoading(true);
    const deleteApi = deleteStatus === "5"
      ? productListApiService.deleteProducts
      : productDraftApiService.deleteProductDraft;

    deleteApi(id)
      .then((res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          fetchDraftsList();
          handleClose();
          message.showToast(deleteStatus === "5" ? "Product deleted" : "Draft deleted", ToastTypes.SUCCESS);
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Server error", ToastTypes.ERROR);
        loader.showLoading(false);
      });
  };

  const tableRows = useMemo(() => {
    return (productErrorData || []).map((item, index) => {
      const { minAp, maxAp, minCp, maxCp } = getVariantMixPrices(item);
      // Get seller info directly from backend response (companyName and sellerOpenId from Product)
      const sellerCompanyName = item.sellerCompanyName || item.companyName || "";
      const sellerOpenId = item.sellerOpenId || "";
      const sellerDisplayName = translatedSellerNames[sellerCompanyName] || sellerCompanyName;
      const sellerDisplay = sellerDisplayName || sellerOpenId || "-";
      
      // Get subscribed users from backend response
      const subscribedUsers = item.subscribedUsers || [];
      const subscriberNames = subscribedUsers.map(u => u.name).filter(n => n && n !== 'Unknown').join(", ");
      
      // Get variant count and category
      const variantCount = (item.variations || []).length;
      const category =  item.categoryPath || item.category || item.categoryName || item.categoryId || "-";
      
      return {
        _id: item._id,
        serialNumber: (page - 1) * limit + index + 1,
        raw: item,
        image: normalizeUrl(item?.thumbnailImage || item?.imgUrl?.[0]),
        title: item.title || "-",
        sellerName: sellerDisplay,
        sellerCompanyName: sellerCompanyName,
        sellerDisplayName: sellerDisplayName,
        sellerOpenId: sellerOpenId,
        subscriberNames: subscriberNames || "-",
        subscriberCount: subscribedUsers.length,
        sku: item.sku || "-",
        variantCount,
        category,
        minAp,
        maxAp,
        minCp,
        maxCp,
        vendor_price: item.vendor_price || 0,
        sp: item.sp || 0,
        status: item.processingStatus || "pending",
        createdAt: item.createdAt,
      };
    });
  }, [productErrorData, page, limit, translatedSellerNames]);

  const columns = useMemo(() => [
    {
      key: "serialNumber",
      label: "#",
      width: 35,
      render: (value) => <span className="pd-serial">{value}</span>,
    },
    {
      key: "image",
      label: "IMAGE",
      width: 65,
      render: (value) => (
        <div className="pd-image-cell">
          {value ? <img src={value} alt="" referrerPolicy="no-referrer" /> : <div className="pd-no-image">-</div>}
        </div>
      ),
    },
    {
      key: "title",
      label: "Product",
      width: 280,
      render: (value, row) => (
        <div className="pd-product-cell">
          <a
            className="pd-product-title"
            href={generateProductUrl({
              id: row.raw?._id,
              offer: row.raw?.offerId,
              isAli: !!row.raw?.offerId,
              title: row.raw?.title,
              variantId: row.raw?.variantId || undefined,
            })}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {value}
          </a>
        </div>
      ),
    },
    {
      key: "sellerName",
      label: "Seller",
      width: 150,
      render: (value, row) => (
        <div className="pd-seller-cell">
          {row.sellerCompanyName ? (
            <>
              <span className="pd-seller-name" title={row.sellerDisplayName || row.sellerCompanyName}>{truncateText(row.sellerDisplayName || row.sellerCompanyName, 25)}</span>
              {row.sellerOpenId && <span className="pd-seller-id">ID: {row.sellerOpenId}</span>}
            </>
          ) : row.sellerOpenId ? (
            <span className="pd-seller-id">ID: {row.sellerOpenId}</span>
          ) : (
            <span className="pd-no-seller">-</span>
          )}
        </div>
      ),
    },
    {
      key: "sku",
      label: "SKU",
      width: 130,
      render: (value) => <span className="pd-sku">{value}</span>,
    },
    {
      key: "variantCount",
      label: "Variants",
      width: 80,
      render: (value) => <span className="pd-variant-count">{value}</span>,
    },
    {
      key: "category",
      label: "Category",
      width: 120,
      render: (value) => <span className="pd-category">{truncateText(value, 20)}</span>,
    },
    {
      key: "vendor_price",
      label: "Vendor Price",
      width: 200,
      render: (_, row) => {
        const fmt = (v) => v != null ? `¥${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;
        const dropshipVal = row.minAp != null
          ? (row.minAp === row.maxAp ? fmt(row.minAp) : `(${fmt(row.minAp)} ~ ${fmt(row.maxAp)})`)
          : "–";
        const cpMin = row.minCp ?? row.maxCp;
        const cpMax = row.maxCp ?? row.minCp;
        const wholesaleVal = cpMin != null || cpMax != null
          ? (cpMin === cpMax ? fmt(cpMin) : `(${fmt(cpMin) ?? "–"} ~ ${fmt(cpMax) ?? "–"})`)
          : "–";
        const priceRangeList = row.raw?.productSaleInfo?.priceRangeList || [];
        const useRangeList = Array.isArray(priceRangeList) && priceRangeList.length > 2;
        return (
          <div className="pd-vendor-price-cell">
            <div className="pd-vendor-block pd-vendor-dropship">
              <span className="pd-vendor-label">Dropship</span>
              <span className="pd-vendor-value">{dropshipVal}</span>
            </div>
            <div className="pd-vendor-block pd-vendor-wholesale">
              <span className="pd-vendor-label">Wholesale</span>
              {useRangeList ? (
                <div className="pd-wholesale-stacked">
                  <div className="pd-wholesale-prices-row">
                    ({priceRangeList.map((r, i) => (
                      <span key={i} className="pd-wholesale-price-item">
                        {fmt(r.price) ?? "–"}{i < priceRangeList.length - 1 ? " ~ " : ""}
                      </span>
                    ))})
                  </div>
                  <div className="pd-wholesale-qty-row">
                    ({priceRangeList.map((r, i) => {
                      const start = r.startQuantity ?? "–";
                      const nextStart = priceRangeList[i + 1]?.startQuantity;
                      const qtyLabel = nextStart ? `${start}-${nextStart - 1}` : `${start}+`;
                      return (
                        <span key={i} className="pd-wholesale-qty-item">
                          ({qtyLabel}){i < priceRangeList.length - 1 ? " - " : ""}
                        </span>
                      );
                    })})
                  </div>
                </div>
              ) : (
                <span className="pd-vendor-value">{wholesaleVal ?? "–"}</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: "sp",
      label: "Selling Price",
      width: 200,
      render: (value, row) => {
        const item = row.raw || {};
        const fmt = (v) => v != null && Number.isFinite(Number(v)) ? formatPrice(Number(v)) : null;
        const vars = item?.variations || [];
        const dropshipPrices = vars.map((v) => v.finalPrice    != null && Number.isFinite(Number(v.finalPrice)) ? Number(v.finalPrice) : null).filter((n) => n != null);
        const wholesalePrices = vars.map((v) => v.wholesaleFinalPrice != null && Number.isFinite(Number(v.wholesaleFinalPrice)) ? Number(v.wholesaleFinalPrice) : null).filter((n) => n != null);
        const minDropship = dropshipPrices.length ? Math.min(...dropshipPrices) : (value != null ? numericValue(value) : null);
        const maxDropship = dropshipPrices.length ? Math.max(...dropshipPrices) : (value != null ? numericValue(value) : null);
        const minWholesale = wholesalePrices.length ? Math.min(...wholesalePrices) : (value != null ? numericValue(value) : null);
        const maxWholesale = wholesalePrices.length ? Math.max(...wholesalePrices) : (value != null ? numericValue(value) : null);
        const dropshipVal = minDropship != null
          ? (minDropship === maxDropship ? fmt(minDropship) : `(${fmt(minDropship)} ~ ${fmt(maxDropship)})`)
          : "–";
        const priceRanges = Array.isArray(item.priceRanges) ? item.priceRanges : [];
        const usePriceRangesForWholesale = priceRanges.length >= 2;
        const wholesaleVal = !usePriceRangesForWholesale && minWholesale != null
          ? (minWholesale === maxWholesale ? fmt(minWholesale) : `(${fmt(minWholesale) ?? "–"} ~ ${fmt(maxWholesale) ?? "–"})`)
          : null;
        return (
          <div className="pd-vendor-price-cell">
            <div className="pd-vendor-block pd-vendor-dropship">
              <span className="pd-vendor-label">Dropship</span>
              <span className="pd-vendor-value">{dropshipVal}</span>
            </div>
            <div className="pd-vendor-block pd-vendor-wholesale">
              <span className="pd-vendor-label">Wholesale</span>
              {usePriceRangesForWholesale ? (
                <div className="pd-wholesale-stacked">
                  <div className="pd-wholesale-prices-row">
                    ({priceRanges.map((r, i) => (
                      <span key={i} className="pd-wholesale-price-item">
                        {fmt(r.finalPrice) ?? "–"}{i < priceRanges.length - 1 ? " ~ " : ""}
                      </span>
                    ))})
                  </div>
                  <div className="pd-wholesale-qty-row">
                    ({priceRanges.map((r, i) => {
                      const start = r.startQuantity ?? "–";
                      const nextStart = priceRanges[i + 1]?.startQuantity;
                      const qtyLabel = nextStart != null ? `${start}-${nextStart - 1}` : `${start}+`;
                      return (
                        <span key={i} className="pd-wholesale-qty-item">
                          ({qtyLabel}){i < priceRanges.length - 1 ? " - " : ""}
                        </span>
                      );
                    })})
                  </div>
                </div>
              ) : (
                <span className="pd-vendor-value">{wholesaleVal ?? "–"}</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      width: 90,
      render: (value) => {
        const getStatusLabel = (status) => {
          if (status === "6") return "Draft";
          if (status === "5") return "List";
          return status || "-";
        };
        return (
          <span className={`pd-status-badge pd-status-${value?.toLowerCase()}`}>
            {getStatusLabel(value)}
          </span>
        );
      },
    },
    {
      key: "subscriberNames",
      label: "Subscribers",
      width: 100,
      render: (value, row) => (
        <div className="pd-subscribers-cell">
          {row.subscriberCount > 0 ? (
            <>
              <span className="pd-subscriber-count">{row.subscriberCount} user(s)</span>
              <span className="pd-subscriber-names" title={value}>{truncateText(value, 30)}</span>
            </>
          ) : (
            <span className="pd-no-subscribers">0</span>
          )}
        </div>
      ),
    },
    {
      key: "createdAt",
      label: "Created",
      width: 95,
      render: (value) => <span className="pd-date">{formatDate(value)}</span>,
    },
    {
      key: "actions",
      label: "Actions",
      width: 100,
      render: (_, row) => {
        const item = row.raw || {};
        return (
          <div className="pd-actions-cell">
            <button className="pd-action-btn pd-action-edit" onClick={(e) => { e.stopPropagation(); handleEdit(item._id, item.status); }} title="Edit"><MdModeEdit /></button>
            {/* <button className="pd-action-btn pd-action-view" onClick={(e) => { e.stopPropagation(); handleView(item._id); }} title="View"><MdDescription /></button> */}
            {/* <button className="pd-action-btn pd-action-dimensions" onClick={(e) => { e.stopPropagation(); handleDimensionsEdit(item); }} title="Edit Dimensions">Edit</button> */}
            <button className="pd-action-btn pd-action-delete" onClick={(e) => { e.stopPropagation(); handleDelete(item._id, item.status); }} title="Delete"><MdDelete /></button>
          </div>
        );
      },
    },
  ], []);

  const toolbarLeftActions = (
    <div className="pd-toolbar-left">
      <div className="pd-search-box">
        <FaSearch className="pd-search-icon" />
        <input type="text" className="pd-search-input" placeholder="Search name, SKU..." name="search" value={filters.search} onChange={handleOnChange} onKeyUp={(e) => e.key === "Enter" && searchDrafts()} />
      </div>
      <select className="pd-filter-select" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
        <option value="">All Status</option>
        <option value="6">Draft</option>
        <option value="5">List</option>
      </select>
      {/* <select className="pd-filter-select" value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })}>
        <option value="">All Sources</option>
        <option value="alibaba">Global</option>
      </select> */}
      <button className="pd-search-btn" onClick={searchDrafts}><FaSearch /> Search</button>
    </div>
  );

  const toolbarRightActions = (
    <div className="pd-toolbar-right">
      {/* Curruntly not used may enable later */}
      {/* <button className="pd-toolbar-btn" onClick={handleOpenSelectedDetails} disabled={!selectedDrafts.length} title="Change Dimensions for Selected Product">Change Dimensions</button> */}
      <button className="pd-refresh-btn" onClick={fetchDraftsList} title="Refresh"><FaSync className={loading ? "pd-spin" : ""} /></button>
      <button className="pd-toolbar-btn" onClick={handleExportCsv} title="Export"><FaDownload /> Export</button>
      <button className="pd-toolbar-btn pd-btn-success" onClick={handleBulkPublish} disabled={!selectedDrafts.length}><FaCheckCircle /> Publish</button>
      <button className="pd-toolbar-btn pd-btn-danger" onClick={handleBulkDelete} disabled={!selectedDrafts.length}><FaTrash /> Delete</button>
      <div className="pd-rows-picker">
        <span>Rows:</span>
        <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
          {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    </div>
  );

  return (
    <div className="pd-page">
      <div className="pd-table-section">
        <ExcelTable
          data={tableRows}
          columns={columns}
          isLoading={initialLoading || loading}
          loadingText="Loading product errors..."
          showToolbarExport={false}
          showToolbarFilter={false}
          showToolbarColumns={true}
          showToolbarFullscreen={true}
          toolbarLeftActions={toolbarLeftActions}
          toolbarRightActions={toolbarRightActions}
          onSelectionChange={(rows) => setSelectedDrafts(rows)}
          selectedData={selectedDrafts}
          selectable={true}
          sortable={true}
          resizable={true}
          showToolbar={true}
          title="Product Errors"
          userId={currentUserId}
          tableName="product-errors"
        />

        {tableRows.length > 0 && Math.ceil(tableRows.length / limit) > 1 && (
          <div className="pd-pagination">
            {(() => {
              const totalFilteredItems = tableRows.length;
              const totalPages = Math.ceil(totalFilteredItems / limit);
              const startItem = (page - 1) * limit + 1;
              const endItem = Math.min(page * limit, totalFilteredItems);
              return (
                <>
                  <div className="pd-pagination-info">Showing {startItem} to {endItem} of {totalFilteredItems} products</div>
                  <div className="pd-pagination-controls">
                    <button className="pd-page-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (page <= 3) pageNum = i + 1;
                      else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = page - 2 + i;
                      return (
                        <button key={pageNum} className={`pd-page-btn ${page === pageNum ? "pd-page-btn-active" : ""}`} onClick={() => setPage(pageNum)}>{pageNum}</button>
                      );
                    })}
                    {totalPages > 5 && page < totalPages - 2 && (
                      <>
                        <span className="pd-page-dots">...</span>
                        <button className="pd-page-btn" onClick={() => setPage(totalPages)}>{totalPages}</button>
                      </>
                    )}
                    <button className="pd-page-btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>›</button>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      <DeletePopUp heading={deleteStatus === "5" ? "Delete Product" : "Delete Draft"} text={deleteStatus === "5" ? "Are you sure you want to delete this product?" : "Are you sure you want to delete this draft?"} open={open} handleClose={handleClose} submitText="Yes" cancelText="No" handleSubmit={handleSubmit} handleCancel={handleClose} />
      
      {/* Export Processing Modal */}
      {exportProcessing && (
        <div className="export-processing-modal-backdrop">
          <div className="export-processing-modal">
            <div className="export-processing-modal-content">
              <div className="export-processing-spinner">
                <FaSync className="pd-spin" style={{ fontSize: '24px', color: '#2563eb' }} />
              </div>
              <div className="export-processing-text">
                <h3>Exporting Data</h3>
                <p>{exportStatus || "Processing..."}</p>
                <div className="export-progress-bar-container">
                  <div className="export-progress-bar">
                    <div 
                      className="export-progress-bar-fill" 
                      style={{ width: `${exportProgress}%` }}
                    ></div>
                  </div>
                  <div className="export-progress-percentage">{exportProgress}%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
